/** Date-only helpers. Never use millisecond Date math across DST. */

export type Ymd = { year: number; month: number; day: number };

export function parseYmd(value: string): Ymd | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!isValidYmd(year, month, day)) return null;
  return { year, month, day };
}

export function formatYmd(date: Ymd): string {
  const month = String(date.month).padStart(2, '0');
  const day = String(date.day).padStart(2, '0');
  return `${date.year}-${month}-${day}`;
}

export function todayYmd(): Ymd {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}

export function isValidYmd(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const probe = new Date(year, month - 1, day);
  return (
    probe.getFullYear() === year &&
    probe.getMonth() === month - 1 &&
    probe.getDate() === day
  );
}

export function addDays(date: Ymd, days: number): Ymd {
  const probe = new Date(date.year, date.month - 1, date.day + days);
  return { year: probe.getFullYear(), month: probe.getMonth() + 1, day: probe.getDate() };
}

export function daysBetween(start: Ymd, end: Ymd): number {
  const a = Date.UTC(start.year, start.month - 1, start.day);
  const b = Date.UTC(end.year, end.month - 1, end.day);
  return Math.round((b - a) / 86_400_000);
}

export function weekdayMonday0(date: Ymd): number {
  const js = new Date(date.year, date.month - 1, date.day).getDay();
  return (js + 6) % 7;
}

export function weekdayName(date: Ymd): string {
  return new Date(date.year, date.month - 1, date.day).toLocaleDateString('en-US', {
    weekday: 'long',
  });
}

export function monthLabel(date: Ymd): string {
  return new Date(date.year, date.month - 1, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}

export function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

export function startOfMonth(date: Ymd): Ymd {
  return { year: date.year, month: date.month, day: 1 };
}

export function addMonths(date: Ymd, months: number): Ymd {
  const probe = new Date(date.year, date.month - 1 + months, 1);
  return { year: probe.getFullYear(), month: probe.getMonth() + 1, day: 1 };
}

export const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
] as const;

export type ParentId = 'A' | 'B';

/** 14-day cycles, index 0 = the anchor date. */
export const ROTATION_PRESETS: Record<string, { label: string; cycle: ParentId[] }> = {
  'week-on-week-off': {
    label: 'Week on / week off',
    cycle: ['A', 'A', 'A', 'A', 'A', 'A', 'A', 'B', 'B', 'B', 'B', 'B', 'B', 'B'],
  },
  '2-2-3': {
    label: '2-2-3',
    cycle: ['A', 'A', 'B', 'B', 'A', 'A', 'A', 'B', 'B', 'A', 'A', 'B', 'B', 'B'],
  },
  '2-2-5-5': {
    label: '2-2-5-5',
    cycle: ['A', 'A', 'B', 'B', 'A', 'A', 'A', 'A', 'A', 'B', 'B', 'B', 'B', 'B'],
  },
  '3-4-4-3': {
    label: '3-4-4-3',
    cycle: ['A', 'A', 'A', 'B', 'B', 'B', 'B', 'A', 'A', 'A', 'A', 'B', 'B', 'B'],
  },
  'every-weekend': {
    label: 'Every weekend (B has Fri–Sun)',
    cycle: ['A', 'A', 'A', 'A', 'B', 'B', 'B', 'A', 'A', 'A', 'A', 'B', 'B', 'B'],
  },
};

export function requireRotation(key: string): { label: string; cycle: ParentId[] } {
  const preset = ROTATION_PRESETS[key];
  if (!preset) {
    throw new Error(`Unknown rotation: ${key}`);
  }
  return preset;
}

export function parentOnDate(
  date: Ymd,
  anchor: Ymd,
  cycle: ParentId[],
): ParentId {
  const offset = daysBetween(anchor, date);
  const index = ((offset % cycle.length) + cycle.length) % cycle.length;
  return cycle[index];
}

export function nextExchangeWeekday(from: Ymd, weekday: number): Ymd {
  const current = weekdayMonday0(from);
  const delta = (weekday - current + 7) % 7;
  return addDays(from, delta);
}
